import React, { Dispatch, SetStateAction } from 'react';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DropzoneArea from '../../common/DropzoneArea';
import LoadingCover from '../../common/LoadingCover';
import { Attachment, PaymentRequisition } from '../../../redux/vmsApi';
import { serializeDatePickerValue } from '../../../utils/dateUtil';

type ConfirmPaymentDialogProps = {
    open: boolean;
    currentPaymentReq: PaymentRequisition | null;
    formData: Partial<PaymentRequisition>;
    referenceNumbersByProcessId: Record<string, string>;
    attachments: Attachment[];
    setAttachments: Dispatch<SetStateAction<Attachment[]>>;
    showOverlay: boolean;
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: () => void;
    onFormChange: (field: keyof PaymentRequisition, value: any) => void;
};

const ConfirmPaymentDialog: React.FC<ConfirmPaymentDialogProps> = ({
    open,
    currentPaymentReq,
    formData,
    referenceNumbersByProcessId,
    attachments,
    setAttachments,
    showOverlay,
    isSubmitting,
    onClose,
    onSubmit,
    onFormChange,
}) => {
    const selectedPurchaseOrder = currentPaymentReq?.purchaseOrder;
    const paymentCurrency = selectedPurchaseOrder?.quotation?.currency || currentPaymentReq?.currency || 'USD';
    const paymentReferenceValue = selectedPurchaseOrder?.quotation?.grandTotal ?? currentPaymentReq?.amount;
    const requisitionReference =
        selectedPurchaseOrder?.purchaseOrderNumber ||
        referenceNumbersByProcessId[currentPaymentReq?.procurementProcessId || ''] ||
        currentPaymentReq?.procurementProcessId ||
        'N/A';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {currentPaymentReq ? 'Confirm Payment' : 'Create Payment Requisition'}
            </DialogTitle>
            <DialogContent>
                {showOverlay && <LoadingCover />}
                <Card
                    elevation={3}
                    sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)',
                        borderLeft: '4px solid #1976d2',
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 24px 0 rgba(0,0,0,0.15)'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center" mb={1}>
                        <ReceiptLongIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: '#1976d2',
                                letterSpacing: '0.5px'
                            }}
                        >
                            Payment Requisition Details
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2, borderColor: 'rgba(25, 118, 210, 0.2)' }} />

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                                <NumbersIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <Box component="span" fontWeight={600}>Reference:</Box> {requisitionReference}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" mb={1.5}>
                                <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <Box component="span" fontWeight={600}>Allocation:</Box>
                                    <Box component="span" display="block" ml={2}>
                                        {selectedPurchaseOrder?.development || currentPaymentReq?.development || 'N/A'},
                                    </Box>
                                    <Box component="span" display="block" ml={2}>
                                        {currentPaymentReq?.project || 'N/A'}
                                    </Box>
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                                <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <Box component="span" fontWeight={600}>Details:</Box> {selectedPurchaseOrder?.title || currentPaymentReq?.title || 'N/A'}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" mb={1.5}>
                                <CurrencyExchangeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <Box component="span" fontWeight={600}>Currency:</Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            bgcolor: '#e3f2fd',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            ml: 1
                                        }}
                                    >
                                        {paymentCurrency}
                                    </Box>
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center">
                                <AttachMoneyIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <Box component="span" fontWeight={600}>PO Value:</Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            color: '#2e7d32',
                                            ml: 1
                                        }}
                                    >
                                        {typeof paymentReferenceValue === 'number' ? paymentReferenceValue.toFixed(2) : 'N/A'}
                                    </Box>
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                <TextField
                    label="Reference Number"
                    fullWidth
                    margin="normal"
                    value={formData.referenceNumber || ''}
                    onChange={(e) => onFormChange('referenceNumber', e.target.value)}
                    required
                />

                <Box display="flex" gap={2} mt={2}>
                    <TextField
                        select
                        label="Payment Method"
                        fullWidth
                        value={formData.paymentMethod || ''}
                        onChange={(e) => onFormChange('paymentMethod', e.target.value)}
                        InputProps={{
                            readOnly: true,
                        }}
                        required
                    >
                        <MenuItem value="Nostro">Nostro</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                    </TextField>

                    <TextField
                        label="Amount"
                        fullWidth
                        type="number"
                        value={formData.amount || 0}
                        onChange={(e) => onFormChange('amount', parseFloat(e.target.value))}
                        InputProps={{
                            readOnly: true,
                        }}
                        required
                    />

                    <TextField
                        label="Rate"
                        fullWidth
                        type="number"
                        value={formData.rate || 0}
                        onChange={(e) => onFormChange('rate', parseFloat(e.target.value))}
                        InputProps={{
                            readOnly: true,
                        }}
                        required
                    />

                    <TextField
                        label="USD Equivalent"
                        fullWidth
                        value={
                            formData.amount && formData.rate && formData.rate > 0
                                ? (formData.amount / formData.rate).toFixed(2)
                                : ''
                        }
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                </Box>

                <Box mt={2}>
                    <DatePicker
                        label="Paid Date"
                        value={formData.paymentDate ? new Date(formData.paymentDate) : null}
                        onChange={(date) => onFormChange('paymentDate', date ? serializeDatePickerValue(date) : '')}
                        slotProps={{
                            textField: { fullWidth: true, required: true }
                        }}
                        maxDate={new Date()}
                    />
                </Box>
                <Grid item xs={12} mt={2}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                        Attachments
                    </Typography>
                    <DropzoneArea
                        setAttachments={setAttachments}
                        attachments={attachments}
                        fieldName='documents'
                    />
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    disabled={!formData.referenceNumber || !formData.paymentMethod || !formData.amount || !formData.paymentDate || isSubmitting}
                >
                    Confirm Payment
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmPaymentDialog;
